<template>
  <div id="app">
    <modalWindow v-if="clientVersion < apiVersion" @close="clearIdeal">
      <div class="titlebar">{{ isMobile ? 'App' : 'Website' }} verouderd!</div>
      <div class="inset">
        U bezit niet de laatste versie van de {{ isMobile ? 'app' : 'website' }}.
        <br><br>
        <template v-if="isMobile">
          <a :href="
            isAndroid
              ? 'https://play.google.com/store/apps/details?id=com.vmapp.doelbeleggen'
              : 'https://apps.apple.com/nl/app/doelbeleggen/id1078257969'
            "
          >Update <u>hier</u> via de
            {{ isAndroid ? 'Google Playstore' : 'Apple Appstore' }}.
          </a>
        </template>
        <template v-else>
          Verwijder de cache uit uw browser.
          U kunt dit doen via instellingen of de toetsen CTRL + F5.
        </template>
        <br><br>
        <div class="grey">
          Versie vereist {{ formatVersion(apiVersion) }},
          huidige {{ formatVersion(clientVersion) }}.
        </div>
        <button @click="clearIdeal">Probeer opnieuw</button>
      </div>
    </modalWindow>
    <div
      id="mainview"
      ref="main"
      v-else-if="sdk.isLoggedin"
      :class="[sdk.isBecome ? 'becomepadding' : '', hasStickyMenu ? 'stickymenu' : '']"
    >
      <MainMenu ref="menu" v-if="currentView != 'Intake'"/>
      <BecomeNote class="printhide" v-if="sdk.isBecome"/>
      <TopMenu v-if="currentView != 'Intake' && role > 7"/>
      <div class="main">
        <modalWindow v-if="transaction && currentView != 'Intake'" @close="clearIdeal">
          <div class="titlebar">Controleren van betaling</div>
          <div class="inset">
            {{idealMessage}}
            <button @click="clearIdeal">OK</button>
          </div>
        </modalWindow>

        <div class="msg" v-if="role == -1 && currentView != 'Intake'">
          <b>E-mail bevestigen</b>
          <p>
            Op dit moment is het e-mailadres van uw account nog niet bevestigd.
            Om opnieuw te kunnen inloggen volgt u de link in onze mail en stelt u een wachtwoord in.
          </p>
        </div>
        <template v-if="currentView == 'Dashboard'">
          <div class="msg" v-for="message in unread.slice(0, 1)" :key="message.id">
            <b>{{message.title}}</b><br>
            <i>{{(new Date(message.created)).toLocaleString()}}</i>
            <p v-if="message.files.length === 0" v-html="message.message" />
            <button
              style="margin: 10px;"
              v-for="(file, index) in message.files" :key="index" @click="download(file)"
            >
              <awesomeIcon icon="upload" />
              {{
                file.replace(/upload\-\d+\-(.*)/, '$1').slice(0, 25)
              }}{{
                file.replace(/upload\-\d+\-(.*)/, '$1').length > 25 ? '&mldr;' : ''
              }}
            </button>
            <span class="close-button-modal" @click="setRead(message.id)">⨯</span>
          </div>
        </template>
        <MandateAlerts
          class="printhide"
          v-if="unreadmandates.length > 0"
          :unreadMandates="unreadmandates"
        />
        <component :is="currentView" />
      </div>
    </div>
    <div v-else>
      <component :is="currentView" />
      <modalWindow v-if="!cookies" @close="clearIdeal">
        <div class="titlebar">Cookies uitgeschakeld</div>
        <div class="inset">
          Om uw beveiligde sessie te kunnen onthouden gebruikt deze site cookies.
          Er worden geen cookies van derden gebruikt.
          Schakel cookies voor deze site in en ververs de pagina.
          <button @click="clearIdeal">Ververs pagina</button>
        </div>
      </modalWindow>
      <modalWindow v-else-if="!internet" @close="clearIdeal">
        <div class="titlebar">Geen internetverbinding</div>
        <div class="inset">
          Deze applicatie gebruikt internet om veilig data op te halen over uw portefeuille.
          Schakel internet in om verder te gaan.
          <button @click="clearIdeal">Ververs pagina</button>
        </div>
      </modalWindow>
      <modalWindow v-else-if="!api" @close="clearIdeal">
        <div class="titlebar">Onderhoud</div>
        <div class="inset">
          We plegen op dit moment onderhoud. We kunnen ieder moment weer beschikbaar zijn.
          <button @click="clearIdeal">Ververs pagina</button>
        </div>
      </modalWindow>
    </div>

    <confirmModal></confirmModal>

    <security v-show="$store.state.openPassword || $store.state.openTwofactor" />
  </div>
</template>

<script>
import { filter } from 'lodash-es';
import Activity from './components/Activity';
import AdminUserCosts from './components/AdminUserCosts';
import Assets from './components/Assets';
import Archive from './components/Archive';
import AtpCustomers from './components/atp/atpCustomers';
import AtpDetails from './components/atp/atpDetails';
import AtpGoals from './components/atp/atpGoals';
import AtpTools from './components/atp/atpTools';
import AtpUsers from './components/atp/atpUsers';
import BecomeNote from './components/ui/becomeNote';
import ConfirmModal from './components/ui/confirm';
import Dashboard from './components/Dashboard';
import Deltas from './components/Deltas';
import DocCenter from './components/atp/docCenter';
import FundDetailScreen from './components/FundDetailScreen';
import GoalLargePurchase from './components/register/goalLargePurchase';
import GoalWealthBuilding from './components/register/goalWealthBuilding';
import Home from './components/Home';
import Intake from './components/Intake';
import Ideals from './components/Ideals';
import Intakes from './components/Intakes';
import IntakesRedos from './components/IntakesRedos';
import IntakesCompleted from './components/IntakesCompleted';
import IntakesDeleted from './components/IntakesDeleted';
import Login from './components/Login';
import Mandates from './components/Mandates';
import MandateAlerts from './components/alerts/mandateAlerts';
import MainMenu from './components/MainMenu';
import Messages from './components/Messages';
import Mutations from './components/Mutations';
import Notifications from './components/Notifications';
import NPS from './components/NPS';
import Offer from './components/offers/offer';
import Payouts from './components/Payouts';
import Payouts2 from './components/Payouts2';
import Positions from './components/Positions';
import Ports from './components/Ports';
import ProfilePage from './components/Profile';
import Profiles from './components/Profiles';
import Register from './components/Register';
import Register2 from './components/register/Register2';
import Reports from './components/Reports';
import RiskCalendar from './components/RiskCalendar';
import RiskModule from './components/RiskModule';
import Sats from './components/Sats';
import Security from './components/profile/Security';
import TopMenu from './components/ui/topMenu';
import Ureturn from './components/Ureturn';
import Users from './components/Users';
import VKM from './components/VKM';

export default {
  name: 'app',
  components: {
    Activity,
    AdminUserCosts,
    Assets,
    Archive,
    AtpCustomers,
    AtpDetails,
    AtpGoals,
    AtpTools,
    AtpUsers,
    BecomeNote,
    ConfirmModal,
    Dashboard,
    Deltas,
    DocCenter,
    FundDetailScreen,
    GoalLargePurchase,
    GoalWealthBuilding,
    Home,
    Ideals,
    Intake,
    Intakes,
    IntakesRedos,
    IntakesDeleted,
    IntakesCompleted,
    Login,
    Mandates,
    MandateAlerts,
    MainMenu,
    Messages,
    Mutations,
    Notifications,
    NPS,
    Offer,
    Payouts,
    Payouts2,
    Ports,
    Positions,
    ProfilePage,
    Profiles,
    Reports,
    RiskCalendar,
    RiskModule,
    Register,
    Register2,
    Sats,
    Security,
    TopMenu,
    Ureturn,
    Users,
    VKM,
  },
  data() {
    return {
      other: false,
      api: true,
      idealMessage: 'We zijn uw betaling aan het controleren...',
      cookies: true,
      loaded: false,
      clientVersion: this.sdk.sdkVersion,
      apiVersion: 0,
      isAndroid: window.navigator.userAgent.toLowerCase().indexOf('android') > -1,
      hasStickyMenu: false,
      transaction: null,
    };
  },
  watch: {
    currentView() {
      if (document.querySelector('#mainview')) {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        document.querySelector('#mainview').scrollTop = 0;
      }
    },
  },
  computed: {
    isMobile() {
      return window.mobile;
    },
    unreadmandates() {
      return filter(this.$store.state.mandates, (o) => o.accepted == null);
    },
    currentView() {
      return this.$store.state.currentView;
    },
    unread() {
      const unreadResult = [];
      for (let i = 0; i < this.$store.state.unread.length ? 1 : 0; i += 1) {
        unreadResult.push(this.$store.state.unread[i]);
        const matches = this.$store.state.unread[i].message.match(/\[file](.*?)\[\/file]/g);
        unreadResult[i].files = [];
        if (matches) {
          for (let j = 0; j < matches.length; j += 1) {
            unreadResult[i].files.push(matches[j].replace(/\[file](.*?)\[\/file]/g, '$1'));
          }
        }
        unreadResult[i].message = unreadResult[i].message.replace(/\[file](.*?)\[\/file]/g, '');
      }
      return unreadResult;
    },
    role() {
      return this.$store.state.role;
    },
    internet() {
      return navigator.onLine;
    },
  },
  created() {
    try {
      const temp = atob(decodeURIComponent(this.getQueryVariable('scope')));
      const scope = temp.split(':');
      if (scope.length === 2) {
        const [type, value] = scope;
        if (type === 'IDEAL') {
          this.transaction = value;
        }
      }
    } catch (e) {
      // no trx
    }
    if (window.mobile) {
      this.isMobile = true;
    }
    if (this.getQueryVariable('utm_medium')) {
      const utms = {};
      utms.utm_medium = this.getQueryVariable('utm_medium') || '';
      utms.utm_source = this.getQueryVariable('utm_source') || '';
      utms.utm_campaign = this.getQueryVariable('utm_campaign') || '';
      utms.utm_content = this.getQueryVariable('utm_content') || '';
      utms.utm_term = this.getQueryVariable('utm_term') || '';
      utms.fid = this.getQueryVariable('fid') || '';
      this.createCookie('utmcookie', JSON.stringify(utms));
    }
    if (this.getQueryVariable('hash') && this.sdk.isLoggedin) {
      // this.sdk.logout();
      // window.location.reload();
    } else if (this.getQueryVariable('hash') && this.getQueryVariable('intake')) {
      this.sdk.loginHash(
        this.getQueryVariable('email'),
        this.getQueryVariable('hash'),
        (response) => {
          if (response.status === 200) {
            window.location.reload();
          } else {
            this.loaded = true;
          }
        },
      );
    } else {
      this.loaded = true;
    }
    this.sdk.ping((response) => {
      if (response.status !== 200) {
        this.api = false;
        this.cookies = this.sdk.cookiesEnabled();
      } else {
        this.apiVersion = response.data.minVersion;
      }
    });
    if (this.transaction) {
      this.sdk.checkIdeal(this.transaction, (response) => {
        if (response.status === 200) {
          if (response.data.status === 2) {
            this.idealMessage = `We hebben uw betaling van € ${this.formatPrice(Number(response.data.amount))} binnen gekregen. Het bedrag zal binnen enkele dagen in uw portefeuille #${response.data.port} zichtbaar zijn.`;
          } else {
            this.idealMessage = 'We hebben uw betaling nog niet binnengekregen, ververs het scherm om nogmaals te controleren.';
          }
        } else if (response.status === 404) {
          this.idealMessage = 'Er is iets fout gegaan met het controleren van deze betaling, neem contact met ons op.';
        }
      });
    }
    this.$store.state.client = this.sdk.client;
    this.$store.state.role = this.sdk.role;
    this.$store.state.nps = this.sdk.nps;
    this.$store.state.name = this.sdk.name;
  },
  methods: {
    formatVersion(version) {
      let formattedVersion = version.toString();
      while (formattedVersion.length < 5) {
        formattedVersion = `0${formattedVersion}`;
      }
      return `${
        formattedVersion.slice(0, -4)
      }.${
        formattedVersion.slice(-4).substring(0, 2)
      }.${
        formattedVersion.slice(-2)
      }`;
    },
    download(file) {
      this.sdk.getPublicFile(file);
    },
    clearIdeal() {
      window.location.href = '?';
    },
    setRead(message) {
      this.sdk.setMessageRead(message, () => {
        this.sdk.getUnreadMessages((response) => {
          if (response.status === 200) {
            this.$store.state.unread = response.data;
          }
        });
      });
    },
  },
};
</script>

<style lang="scss">
  @import "scss/style.scss";
  body {
    background-color: $color-grey;
    margin: 0;
  }
</style>

<style lang="scss" scoped>
  .becomepadding {
    @media not print {
      margin-top: 35px;
    }
  }
  .msg {
    @media #{$mobile} {
      margin: 10px 10px 0;
    }
    @media #{$desktop} {
      margin: 20px auto;
    }
  }
  #mainview {
    background-color: $color-grey;
    @media screen and (max-width: 988px) {
      width: 100%;
      padding-top: 55px;
    }
    @media #{$desktop} {
      position: fixed;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      z-index: 0;
      overflow-y: auto;
      // padding: 20px;
      .main {
        margin: 20px;
        // margin-top: 76px;
        &::after {
          content: "";
          clear: both;
          display: table;
        }
      }
    }
  }
  // weird value for FF
  @media screen and (min-width: 988.1px) {
    #mainview {
      @media screen and (max-width: 1440px) {
        padding-left: 240px;
        padding-right: 20px;
      }
      .main {
        margin-left: auto;
        margin-right: auto;
        max-width: 960px;
      }
    }
  }
</style>
